import { useState, useEffect } from 'react';

interface MobileAppRedirectOptions {
	deepLink?: string;
	webFallbackLink?: string;
	dtLink?: string;
}

interface MobileAppRedirectResult {
	isMobileWeb: boolean;
	isIOS: boolean;
	isAndroid: boolean;
	handleRedirect: (options: MobileAppRedirectOptions) => void;
	getRedirectUrl: (options: MobileAppRedirectOptions) => string;
}

const useMobileAppRedirect = (): MobileAppRedirectResult => {
	const [isMobileWeb, setIsMobileWeb] = useState(false);
	const [isIOS, setIsIOS] = useState(false);
	const [isAndroid, setIsAndroid] = useState(false);
	const [appInstallCache, setAppInstallCache] = useState<Map<string, { installed: boolean; timestamp: number }>>(new Map());

	// Cache management constants
	const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
	const STORAGE_KEY = 'mobile_app_redirect_cache';

	const loadCacheFromStorage = () => {
		try {
			const stored = localStorage.getItem(STORAGE_KEY);
			if (stored) {
				const parsed = JSON.parse(stored);
				const cacheMap = new Map();

				// Filter out expired entries
				Object.entries(parsed).forEach(([key, value]: [string, any]) => {
					if (Date.now() - value.timestamp < CACHE_DURATION) {
						cacheMap.set(key, value);
					}
				});

				setAppInstallCache(cacheMap);
			}
		} catch (error) {
			// Ignore storage errors
		}
	};

	useEffect(() => {
		const userAgent = navigator.userAgent;

		// Detect mobile platforms
		const iosDetection = /iPhone|iPad|iPod/i.test(userAgent);
		const androidDetection = /Android/i.test(userAgent);
		const isMobileDevice = iosDetection || androidDetection || window.innerWidth < 768;

		// Detect if we're in a mobile web browser (not in-app)
		const isInWebBrowser = !!(
			// Not in WebView or in-app browser
			(
				!/wv|WebView/i.test(userAgent) &&
				// Not standalone PWA
				!(window.navigator as any).standalone &&
				// Standard browser indicators present
				(window.location.protocol === 'http:' || window.location.protocol === 'https:')
			)
		);

		setIsMobileWeb(isMobileDevice && isInWebBrowser);
		setIsIOS(iosDetection);
		setIsAndroid(androidDetection);

		// Load cached app install data from localStorage
		loadCacheFromStorage();
	}, []);

	const saveCacheToStorage = (cache: Map<string, { installed: boolean; timestamp: number }>) => {
		try {
			const cacheObj = Object.fromEntries(cache);
			localStorage.setItem(STORAGE_KEY, JSON.stringify(cacheObj));
		} catch (error) {
			// Ignore storage errors (private mode, quota exceeded, etc.)
		}
	};

	const getCachedResult = (deepLink: string): boolean | null => {
		const cached = appInstallCache.get(deepLink);
		if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
			return cached.installed;
		}
		return null;
	};

	const setCachedResult = (deepLink: string, installed: boolean) => {
		const newCache = new Map(appInstallCache);
		newCache.set(deepLink, { installed, timestamp: Date.now() });
		setAppInstallCache(newCache);
		saveCacheToStorage(newCache);
	};

	// Simple app installation check using visibility API
	const checkAppInstalled = (deepLink: string): Promise<boolean> => {
		return new Promise((resolve) => {
			let resolved = false;

			const resolveOnce = (result: boolean) => {
				if (!resolved) {
					resolved = true;
					resolve(result);
				}
			};

			// Set timeout for fallback
			const timer = setTimeout(() => resolveOnce(false), 700);

			// Listen for page visibility change (app opened)
			const handleVisibilityChange = () => {
				if (document.hidden) {
					clearTimeout(timer);
					document.removeEventListener('visibilitychange', handleVisibilityChange);
					resolveOnce(true);
				}
			};

			document.addEventListener('visibilitychange', handleVisibilityChange);

			// Use iframe for both iOS and Android to avoid popup warnings
			const iframe = document.createElement('iframe');
			iframe.style.cssText = 'display:none;position:absolute;width:1px;height:1px;top:-1000px;';
			document.body.appendChild(iframe);

			const cleanup = () => {
				if (document.body.contains(iframe)) {
					document.body.removeChild(iframe);
				}
				document.removeEventListener('visibilitychange', handleVisibilityChange);
			};

			// Cleanup after timeout
			setTimeout(cleanup, 1000);

			iframe.src = deepLink;
		});
	};

	const handleRedirect = async (options: MobileAppRedirectOptions) => {
		const { deepLink, webFallbackLink, dtLink } = options;

		// Desktop: open in new tab
		if (!isMobileWeb) {
			if (dtLink) {
				window.open(dtLink, '_blank');
			}
			return;
		}

		// Mobile web: check cache first, then app installation
		if (deepLink) {
			try {
				// Check cache first for instant response
				const cachedResult = getCachedResult(deepLink);
				let isAppInstalled: boolean;

				if (cachedResult !== null) {
					// Use cached result (instant!)
					isAppInstalled = cachedResult;
				} else {
					// No cache, perform fresh check
					isAppInstalled = await checkAppInstalled(deepLink);
					// Cache the result for future use
					setCachedResult(deepLink, isAppInstalled);
				}

				if (isAppInstalled) {
					// App is installed, open it
					window.location.href = deepLink;
				} else {
					// App not installed, use web fallback
					if (webFallbackLink) {
						window.location.href = webFallbackLink;
					} else if (dtLink) {
						window.location.href = dtLink;
					}
				}
			} catch (error) {
				// Error checking app, fallback to web
				if (webFallbackLink) {
					window.location.href = webFallbackLink;
				} else if (dtLink) {
					window.location.href = dtLink;
				}
			}
		} else {
			// No deep link, use web fallback
			if (webFallbackLink) {
				window.location.href = webFallbackLink;
			} else if (dtLink) {
				window.location.href = dtLink;
			}
		}
	};

	const getRedirectUrl = (options: MobileAppRedirectOptions): string => {
		const { deepLink, webFallbackLink, dtLink } = options;

		// For href attribute, always return web link to avoid browser warnings
		if (!isMobileWeb) {
			return dtLink || webFallbackLink || '#';
		}

		// On mobile web, return web fallback for href (actual redirect handled by handleRedirect)
		return webFallbackLink || dtLink || deepLink || '#';
	};

	return {
		isMobileWeb,
		isIOS,
		isAndroid,
		handleRedirect,
		getRedirectUrl,
	};
};

export default useMobileAppRedirect;
